/**
 * Map Postgres / Supabase errors to safe, user-facing API responses.
 *
 * NEVER return raw `error.message` from the database to clients — log the raw
 * error with your logger (e.g. `@wtc/logger` `logger.error(message, error, ctx)`)
 * and return one of these mapped messages instead.
 *
 * This is the CANONICAL, cross-app home for `mapToUserFacingError`. It was
 * promoted here from `apps/hub` so that other WTC apps (Nonprofit Hub, etc.)
 * can enforce the same `USER_FACING_API_ERRORS` posture without importing
 * another app's code (which is forbidden — see HUB_VS_NONPROFIT_HUB_SEPARATION).
 */

export interface PostgresLikeError {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
}

export type UserFacingErrorBody = {
  error: string;
  code: string;
};

export type UserFacingErrorResult = UserFacingErrorBody & {
  status: number;
};

type CodeOverride = UserFacingErrorResult;

/**
 * An app-supplied refusal parser, run BEFORE the generic SQLSTATE branches.
 *
 * This is the extension point that lets an app keep a domain-specific 409 (which
 * knows WHO holds a conflicting value and which of the fixes applies) without
 * forking this file: a generic 23505 would flatten it to "A record with these
 * details already exists.", which is useless in those cases. Return `null` when
 * the error isn't yours and the next pre-check — then the generic mapping —
 * gets a turn. The package stays ignorant of any app's domain types.
 */
export type UserFacingPreCheck = (error: unknown) => UserFacingErrorResult | null;

export interface MapToUserFacingErrorContext {
  /** Human phrase completing "We couldn't complete this ___." (e.g. "request"). */
  operation: string;
  /** Per-Postgres-code overrides, keyed by SQLSTATE (e.g. "23505"). */
  overrides?: Partial<Record<string, CodeOverride>>;
  /**
   * App-specific parsers tried in order, first non-null wins, ahead of every
   * built-in branch. Omit it and the mapping behaves exactly as it always has.
   */
  preChecks?: readonly UserFacingPreCheck[];
}

function isPostgresLikeError(error: unknown): error is PostgresLikeError {
  return (
    typeof error === "object" &&
    error !== null &&
    ("code" in error || "message" in error)
  );
}

export function mapToUserFacingError(
  error: unknown,
  context: MapToUserFacingErrorContext,
): UserFacingErrorResult {
  // App-specific refusals first: they carry the actionable detail (WHO holds the
  // value, which of the two fixes applies) that the generic branches below —
  // 23505 in particular — would flatten away.
  if (context.preChecks) {
    for (const preCheck of context.preChecks) {
      const preChecked = preCheck(error);
      if (preChecked) return preChecked;
    }
  }

  if (error instanceof Error && error.message.includes("has no step 1")) {
    return {
      status: 422,
      code: "SEQUENCE_NO_STEPS",
      error:
        "This cadence has no steps configured yet. Add steps in the sequence editor before enrolling contacts.",
    };
  }

  if (isPostgresLikeError(error) && error.code) {
    const override = context.overrides?.[error.code];
    if (override) return override;

    switch (error.code) {
      case "23505":
        return {
          status: 409,
          code: "DUPLICATE_RECORD",
          error: "A record with these details already exists.",
        };
      case "23503":
        return {
          status: 404,
          code: "REFERENCED_RECORD_MISSING",
          error:
            "One of the selected items no longer exists. Refresh the page and try again.",
        };
      case "23502":
        return {
          status: 400,
          code: "REQUIRED_FIELD_MISSING",
          error:
            "Required information is missing. Check your selections and try again.",
        };
      case "42501":
        return {
          status: 403,
          code: "ACCESS_DENIED",
          error: "You don't have permission to perform this action.",
        };
      case "22P02":
        return {
          status: 400,
          code: "INVALID_DATA",
          error: "Some of the provided data isn't in a valid format.",
        };
      default:
        break;
    }
  }

  return {
    status: 500,
    code: "OPERATION_FAILED",
    error: `We couldn't complete this ${context.operation}. Please try again.`,
  };
}
